import dayjs from "dayjs";
import {
  HalfYearExtensionReason,
  YesOrNoFlug,
} from "../../../assets/data/Constants";
import { IAPI_STA034_Response } from "../../../type/apiType";
import { TkGetCode } from "../../../utils/TkGetCode";
import { monthMapping } from "../parts";
import { SixMonthsSalaryBeforeApplyList } from "../../../type/apiType/IAPI_STA034";

/**
 * 育休テーブルを作成する
 * @param res
 * @returns infoBodyList
 */
function funInfoBodyList(res: any) {
  const infoBodyList: any[] = [];

  for (let i in res) {
    const data = [
      {
        children: res[i].birthday, //出産日
        align: "center",
      },
      {
        children: res[i].startDate, //育児休業開始日
        align: "center",
      },
      {
        children: res[i].endDate, //育児休業終了日
        align: "center",
      },
      {
        children: TkGetCode(YesOrNoFlug, res[i].papaMamaPlus), //パパママ育休プラス
        align: "center",
      },
      {
        children: TkGetCode(
          HalfYearExtensionReason,
          res[i].halfyearExtensionReason
        ), //半年延長の理由
      },
      {
        children: TkGetCode(YesOrNoFlug, res[i].papaChildcareLeaveFlg), //産後パパ育休
        align: "center",
      },
    ];

    infoBodyList.push(data);
  }

  return infoBodyList;
}

/**
 * 処理履歴を作成する
 * @param res
 * @returns statusBodyList
 */
function funStatusBodyList(res: any) {
  const statusBodyList: any[] = [];
  for (let i in res) {
    let createDate = new Date(res[i].createDate);
    const data = [
      {
        children: res[i].managerName,
      },
      {
        children: res[i].process,
      },
      {
        children:
          createDate.getFullYear() +
          "/" +
          (createDate.getMonth() + 1) +
          "/" +
          createDate.getDate(),
        align: "center",
      },
      {
        children: res[i].processComment,
      },
    ];
    statusBodyList.push(data);
  }

  return statusBodyList;
}

/**
 * 異動履歴を作成する
 * @param res
 * @returns transferHistoryBodyList
 */
function funTransferHistoryList(res: any) {
  const transferHistoryBodyList: any[] = [];
  for (let i in res) {
    const data = {
      leftText: (res[i].joinDate ?? "") + "～" + (res[i].transferDate ?? ""),
      rightText: (res[i].facilityNo ?? "") + ":" + (res[i].facilityName ?? ""),
    };
    transferHistoryBodyList.push(data);
  }

  return transferHistoryBodyList;
}

function makeApprovalInfoList(res: IAPI_STA034_Response) {
  const approvalInfoList = [
    [
      {
        children: res.symbolNo,
      },
      {
        children: res.decisionDate,
      },
    ],
  ];

  return approvalInfoList;
}

function monthsArrayToUnrecognizedMonthList(monthsArray: any) {
  let monthList: { [key: string]: string } = {};

  let numberToMonth: { [key: string]: string } = {};
  for (let month in monthMapping) {
    numberToMonth[monthMapping[month]] = month;
  }

  Object.values(monthMapping).forEach((monthNumber: any) => {
    const monthName = numberToMonth[monthNumber];
    monthList[monthName] = monthsArray.includes(monthNumber) ? "1" : "0";
  });

  return monthList;
}

const makeSalaryList = (
  res: IAPI_STA034_Response
): SixMonthsSalaryBeforeApplyList[] => {
  const joinDate = dayjs(res.firstJoinDate).startOf("month");
  const retireDate = dayjs(res.retireDate);
  const isEndOfMonth = retireDate.isSame(retireDate.endOf("month"), "day");
  const target = isEndOfMonth ? retireDate : retireDate.subtract(1, "month");
  const startDate = target.startOf("month");
  const list = [];
  const notApplicableList = res.movementHistoryList?.filter(
    (item) => item.facilityNo === "999"
  );

  let currentDate = startDate;
  while (currentDate.isAfter(joinDate) || currentDate.isSame(joinDate)) {
    const yearMonth = currentDate.format("YYYYMM");
    let data = {
      yearMonth: Number(yearMonth),
      salaryTableAmt: 0,
      salaryAdjustmentAmt: 0,
    };

    let smsba = res.sixMonthsSalaryBeforeApplyList?.find(
      (item) => item.yearMonth === data.yearMonth
    );
    if (smsba) {
      data.salaryTableAmt = smsba.salaryTableAmt ?? 0;
      data.salaryAdjustmentAmt = smsba.salaryAdjustmentAmt ?? 0;
    } else {
      const currentYear =
        currentDate.month() >= 3 ? currentDate.year() : currentDate.year() - 1;
      let a = res.salaryInfoList?.find((item) => item.year === currentYear);
      if (a) {
        data.salaryTableAmt = a.salaryTableAmt ?? 0;
        data.salaryAdjustmentAmt = a.salaryAdjustmentAmt ?? 0;
      }
    }

    // 対象外期間を除外
    const result = notApplicableList?.some((item) => {
      const fromDate = dayjs(item.joinDate, "YYYY/MM/DD").subtract(1, "day");
      const toDate = dayjs(item.transferDate, "YYYY/MM/DD");

      const monthStart = dayjs(yearMonth + "01", "YYYYMMDD");
      const monthEnd = monthStart.endOf("month");

      // 完全に範囲内にある月
      if (
        (monthStart.isSame(fromDate) || monthStart.isAfter(fromDate)) &&
        (monthEnd.isSame(toDate) || monthEnd.isBefore(toDate))
      ) {
        return true;
      }

      // 対象外異動・復帰が同年同月の場合
      if (
        fromDate.isSame(toDate, "month") &&
        monthStart.isSame(fromDate, "month")
      ) {
        const daysBeforeFrom = fromDate.date() - 1;
        const daysAfterTo = toDate.endOf("month").date() - toDate.date();
        // その月の出勤日数の合計が10日以下の場合
        if (daysBeforeFrom + daysAfterTo <= 10) {
          return true;
        } else {
          return false;
        }
      }

      // 対象外異動日がその月の10日以前の場合
      if (monthStart.isSame(fromDate, "month") && fromDate.date() <= 10) {
        return true;
      }

      // 復帰日からその月の月末までが10日以下の場合
      if (monthStart.isSame(toDate, "month")) {
        const daysAfterTo = toDate.endOf("month").date() - toDate.date();
        if (daysAfterTo <= 10) {
          return true;
        }
      }

      return false;
    });

    if (!result) {
      list.push(data);
    }
    currentDate = currentDate.subtract(1, "month");
  }

  return list;
};

export {
  funInfoBodyList,
  funStatusBodyList,
  funTransferHistoryList,
  makeApprovalInfoList,
  monthsArrayToUnrecognizedMonthList,
  makeSalaryList,
};
